import React from "react";

function titleCase(key: string): string {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^./, (c) => c.toUpperCase())
    .trim();
}

function tryParseJson(value: unknown): unknown | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== "{" && trimmed[0] !== "[")) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function renderPrimitive(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
}

/**
 * Renders a free-text field that might actually be a JSON string - e.g. a
 * catalog-sourced prescription's ordered items, stored as a serialized
 * array/object in `notes` - as a friendly list/table instead of a raw
 * stringified blob. Falls back to plain text if it isn't JSON at all.
 */
export function StructuredNote({ value }: { value: unknown }): JSX.Element | null {
  if (value === null || value === undefined || value === "") return null;

  const parsed =
    tryParseJson(value) ?? (isPlainObject(value) || Array.isArray(value) ? value : null);

  if (parsed === null) {
    return <p className="text-gray-600 whitespace-pre-wrap">{String(value)}</p>;
  }

  if (Array.isArray(parsed)) {
    if (parsed.length === 0) return null;
    return (
      <ul className="list-disc list-inside space-y-1.5">
        {parsed.map((item, idx) => (
          <li key={idx} className="text-sm text-gray-700">
            {isPlainObject(item) ? (
              <span>
                {String(
                  item.name ?? item.drugName ?? item.title ?? `Item ${idx + 1}`
                )}
                {item.quantity ? ` × ${item.quantity}` : ""}
                {item.dosage ? ` — ${item.dosage}` : ""}
                {item.frequency ? ` (${item.frequency})` : ""}
              </span>
            ) : (
              String(item)
            )}
          </li>
        ))}
      </ul>
    );
  }

  if (isPlainObject(parsed)) {
    const entries = Object.entries(parsed).filter(
      ([, v]) => v !== null && v !== undefined && v !== ""
    );
    if (entries.length === 0) return null;
    return (
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
        {entries.map(([key, val]) => (
          <div key={key}>
            <dt className="text-xs font-semibold text-gray-500">{titleCase(key)}</dt>
            <dd className="text-sm text-gray-700">
              {isPlainObject(val) || Array.isArray(val) ? (
                <StructuredNote value={val} />
              ) : (
                renderPrimitive(val)
              )}
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return <p className="text-gray-600">{renderPrimitive(parsed)}</p>;
}
